import React, { useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { PostcodeInput } from 'common/components';
import styles from './index.module.scss';
import cn from 'classnames';

interface CompareRangeProps {
  heading?: string;
  headingTooltip?: string;
  subHeading?: string;
  children?: React.ReactNode;
  headingTag: keyof JSX.IntrinsicElements;
  style?: React.CSSProperties;
}

export const CompareRangeHeader = React.memo<CompareRangeProps>(
  ({ heading, headingTooltip, subHeading, children, style }) => {
    const [referenceElement, setReferenceElement] =
      useState<HTMLElement | null>();
    const subHeadingContainerRef = useRef<HTMLDivElement>(null);
    const [popperElement, setPopperElement] = useState<HTMLElement | null>();
    const [arrowElement, setArrowElement] = useState<HTMLElement | null>();
    const { styles: popperStyles, attributes } = usePopper(
      referenceElement,
      popperElement,
      {
        modifiers: [{ name: 'arrow', options: { element: arrowElement } }],
        placement: 'auto',
      }
    );
    const match = heading?.match(/<h([1-6])[^>]*>/i);
    const tag = match ? `h${match[1]}` : 'p';

    const showTooltip = () => {
      popperElement?.setAttribute('data-show', 'true');
    };

    const hideTooltip = () => {
      popperElement?.removeAttribute('data-show');
    };

    useEffect(() => {
      const container = subHeadingContainerRef.current;

      if (!container) return;

      const handleCTAClick = (e) => {
        const target = e.target.closest('a'); // in case a child element (like <span>) is clicked

        if (
          target &&
          target.tagName === 'A' &&
          target.getAttribute('href')?.includes('#')
        ) {
          const link = document.createElement('a');
          link.href = target.getAttribute('href');
          const elementId = link.hash.substring(1);
          const scrollToElement = document.getElementById(elementId);

          if (scrollToElement) {
            e.preventDefault(); // Prevent default only if element exists
            scrollToElement.scrollIntoView({ behavior: 'smooth' });
          }
          // else: do nothing, allow default behavior (redirect to #id which doesn’t exist)
        }
      };

      container.addEventListener('click', handleCTAClick);

      return () => {
        container.removeEventListener('click', handleCTAClick);
      };
    }, [subHeadingContainerRef.current]);

    return (
      <div className={styles.compareContent} style={style}>
        <div>
          <div
            className={styles.compareTitle}
            dangerouslySetInnerHTML={{ __html: heading ?? '' }}
          />
          {!!headingTooltip && (
            <>
              <span
                ref={setReferenceElement}
                onMouseEnter={showTooltip}
                onMouseLeave={hideTooltip}
                className={cn(styles.disclaimerTrigger, tag)}
              >
                *
              </span>
              <div
                ref={setPopperElement}
                style={popperStyles.popper}
                {...attributes.popper}
                className={styles.disclaimerTooltip}
              >
                <div dangerouslySetInnerHTML={{ __html: headingTooltip }} />
                <div ref={setArrowElement} style={popperStyles.arrow} />
              </div>
            </>
          )}
        </div>
        {subHeading && (
          <div
            ref={subHeadingContainerRef}
            className={styles.compareSubTitle}
            dangerouslySetInnerHTML={{ __html: subHeading ?? '' }}
          />
        )}
        {children}
      </div>
    );
  }
);

export const CompareRangeHeaderWithPostcode = React.memo<CompareRangeProps>(
  ({ heading, subHeading, ...props }) => {
    return (
      <CompareRangeHeader {...props} heading={heading} subHeading={subHeading}>
        <PostcodeInput />
      </CompareRangeHeader>
    );
  }
);
